import { Tooltip } from '@mantine/core';
import {
  IconBook,
  IconBinaryTree,
  IconChartBar,
  IconDashboard,
  IconInbox,
  IconLock,
  IconMail,
  IconMessage,
  IconPackage,
  IconSend,
  IconSettings,
  IconSolarPanel,
  IconTruck,
  IconUser,
  IconUsers,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAppSwitcherContext } from '../AppSwitcherProvider';
import { useNavigationOptions } from '../../hooks/useNavigationOptions';
import { useRoute } from '../../hooks/useRoute';
import { useSidebarContext } from './SidebarProvider';
import sidebarClasses from './Sidebar.module.css';

const ICON_MAP: Record<string, React.ReactNode> = {
  user: <IconUser size={18} />,
  users: <IconUsers size={18} />,
  message: <IconMessage size={18} />,
  settings: <IconSettings size={18} />,
  mail: <IconMail size={18} />,
  dashboard: <IconDashboard size={18} />,
  truck: <IconTruck size={18} />,
  package: <IconPackage size={18} />,
  send: <IconSend size={18} />,
  inbox: <IconInbox size={18} />,
  'chart-bar': <IconChartBar size={18} />,
  'binary-tree': <IconBinaryTree size={18} />,
  book: <IconBook size={18} />,
  // Sidecar icons. Add a key here when registering a new sidecar
  // sidebar entry — the registry in `client/src/sidecars/tiles.ts`
  // references icons by string key so the sidebar's icon set stays
  // explicit and tree-shakable.
  'solar-panel': <IconSolarPanel size={18} />,
  lock: <IconLock size={18} />,
};

export const SidebarNavItems = () => {
  const { app } = useAppSwitcherContext();
  const options = useNavigationOptions();
  const { activeLink } = useRoute();
  const { expanded, closeMobile } = useSidebarContext();
  const navigate = useNavigate();

  const handleClick = (route: string) => {
    navigate(`/${app}${route}`);
    closeMobile();
  };

  return (
    <div
      style={{
        padding: '4px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {options.map(opt => {
        const isActive = activeLink === opt.value;
        const icon = opt.icon ? (
          ICON_MAP[opt.icon] || <IconSettings size={18} />
        ) : (
          <IconSettings size={18} />
        );
        const content = (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleClick(opt.route)}
            className={!isActive ? sidebarClasses.hoverItem : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: expanded ? '9px 12px' : '9px 0',
              justifyContent: expanded ? 'flex-start' : 'center',
              border: 'none',
              borderRadius: 8,
              background: isActive ? '#f4f0ff' : 'transparent',
              color: isActive ? '#6b46e3' : '#334155',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: isActive ? 600 : 500,
            }}
          >
            <span style={{ flexShrink: 0, display: 'flex' }}>{icon}</span>
            {expanded && <span>{opt.label}</span>}
          </button>
        );

        if (!expanded) {
          return (
            <Tooltip
              key={opt.value}
              label={opt.label}
              position="right"
              withArrow
            >
              {content}
            </Tooltip>
          );
        }

        return <div key={opt.value}>{content}</div>;
      })}
    </div>
  );
};
